import React from 'react';

const ResultsSummary = ({ currentDebt, generateFullSchedule, formatCurrency }) => {
  // Only show for traditional loans with full schedule
  if (!currentDebt || currentDebt.type === 'credit-card' || !currentDebt.fullSchedule.length) {
    return null;
  }

  // Calculate original loan without additional payments
  const originalSchedule = generateFullSchedule(currentDebt.data, {
    recurring_extra: 0,
    custom_payments: {},
  });
  const currentSchedule = currentDebt.fullSchedule;

  const originalPayoffDate = originalSchedule[originalSchedule.length - 1]?.date || '';
  const newPayoffDate = currentSchedule[currentSchedule.length - 1]?.date || '';
  const originalTotalInterest = originalSchedule.reduce((sum, p) => sum + p.interest, 0);
  const newTotalInterest = currentSchedule.reduce((sum, p) => sum + p.interest, 0);
  const interestSaved = originalTotalInterest - newTotalInterest;
  const monthsSaved = originalSchedule.length - currentSchedule.length;
  const hasAdditionalPayments =
    currentDebt.additionalPayments.recurring_extra > 0 ||
    Object.keys(currentDebt.additionalPayments.custom_payments).length > 0;

  // Savings Result Container - dark theme for readability
  const savingsResultContainer = React.createElement(
    'div',
    {
      className: 'bg-surface-muted border-2 border-border rounded-lg p-6',
    },
    [
      // Title
      React.createElement(
        'h3',
        {
          key: 'title',
          className: 'text-2xl font-bold mb-6 text-text-primary text-center',
        },
        '💰 SAVINGS RESULT'
      ),

      // Three-column grid
      React.createElement(
        'div',
        {
          key: 'grid',
          className: 'grid grid-cols-1 md:grid-cols-3 gap-6 text-center',
        },
        [
          // Final Payment Date
          React.createElement(
            'div',
            {
              key: 'final-payment',
              className: 'bg-card p-4 rounded-lg border border-border',
            },
            [
              React.createElement(
                'div',
                {
                  key: 'header',
                  className: 'text-sm font-medium text-text-primary mb-3',
                },
                '📅 FINAL PAYMENT'
              ),
              React.createElement(
                'div',
                {
                  key: 'dates',
                  className: 'space-y-2',
                },
                [
                  React.createElement('div', { key: 'original' }, [
                    React.createElement(
                      'div',
                      {
                        key: 'label',
                        className: 'text-xs text-text-muted',
                      },
                      hasAdditionalPayments ? 'Original Schedule:' : 'Current Schedule:'
                    ),
                    React.createElement(
                      'div',
                      {
                        key: 'value',
                        className: 'font-bold text-text-primary',
                      },
                      originalPayoffDate
                    ),
                  ]),
                  hasAdditionalPayments &&
                    React.createElement('div', { key: 'new' }, [
                      React.createElement(
                        'div',
                        {
                          key: 'label',
                          className: 'text-xs text-primary',
                        },
                        'With Additional Payments:'
                      ),
                      React.createElement(
                        'div',
                        {
                          key: 'value',
                          className: 'font-bold text-primary',
                        },
                        newPayoffDate
                      ),
                    ]),
                ]
              ),
            ]
          ),

          // Loan Reduced By
          React.createElement(
            'div',
            {
              key: 'loan-reduced',
              className: 'bg-card p-4 rounded-lg border border-border',
            },
            [
              React.createElement(
                'div',
                {
                  key: 'header',
                  className: 'text-sm font-medium text-text-primary mb-3',
                },
                '⏰ LOAN REDUCED BY'
              ),
              React.createElement(
                'div',
                {
                  key: 'time-saved',
                  className:
                    'text-2xl font-bold ' +
                    (hasAdditionalPayments ? 'text-primary' : 'text-text-muted'),
                },
                hasAdditionalPayments ? monthsSaved + ' months' : '0 months'
              ),
              React.createElement(
                'div',
                {
                  key: 'help-text',
                  className: 'text-xs text-text-muted mt-1',
                },
                hasAdditionalPayments
                  ? 'Time saved with extra payments'
                  : 'Add extra payments to save time'
              ),
            ]
          ),

          // Total Interest
          React.createElement(
            'div',
            {
              key: 'total-interest',
              className: 'bg-card p-4 rounded-lg border border-border',
            },
            [
              React.createElement(
                'div',
                {
                  key: 'header',
                  className: 'text-sm font-medium text-text-primary mb-3',
                },
                '💸 TOTAL INTEREST'
              ),
              React.createElement(
                'div',
                {
                  key: 'interest-amounts',
                  className: 'space-y-2',
                },
                [
                  React.createElement('div', { key: 'original' }, [
                    React.createElement(
                      'div',
                      {
                        key: 'label',
                        className: 'text-xs text-text-muted',
                      },
                      hasAdditionalPayments ? 'Original Total:' : 'Current Total:'
                    ),
                    React.createElement(
                      'div',
                      {
                        key: 'value',
                        className: 'font-bold text-destructive',
                      },
                      '$' + formatCurrency(originalTotalInterest)
                    ),
                  ]),
                  hasAdditionalPayments &&
                    React.createElement('div', { key: 'new' }, [
                      React.createElement(
                        'div',
                        {
                          key: 'label',
                          className: 'text-xs text-primary',
                        },
                        'With Additional:'
                      ),
                      React.createElement(
                        'div',
                        {
                          key: 'value',
                          className: 'font-bold text-primary',
                        },
                        '$' + formatCurrency(newTotalInterest)
                      ),
                    ]),
                ]
              ),
            ]
          ),
        ]
      ),

      // Interest Saved Highlight
      React.createElement(
        'div',
        {
          key: 'interest-saved',
          className:
            'mt-6 rounded-lg p-6 text-center ' +
            (hasAdditionalPayments
              ? 'bg-primary text-primary-foreground'
              : 'bg-surface border border-border'),
        },
        [
          React.createElement(
            'div',
            {
              key: 'title',
              className:
                'text-lg font-medium mb-2 ' + (hasAdditionalPayments ? '' : 'text-text-primary'),
            },
            '💰 TOTAL INTEREST SAVED'
          ),
          React.createElement(
            'div',
            {
              key: 'amount',
              className: 'text-4xl font-bold ' + (hasAdditionalPayments ? '' : 'text-text-primary'),
            },
            '$' + formatCurrency(Math.max(0, interestSaved))
          ),
          React.createElement(
            'div',
            {
              key: 'percentage',
              className:
                'text-sm mt-2 ' + (hasAdditionalPayments ? 'opacity-90' : 'text-text-muted'),
            },
            hasAdditionalPayments
              ? "That's " +
                  ((interestSaved / originalTotalInterest) * 100).toFixed(1) +
                  '% savings!'
              : 'Add additional payments to see savings!'
          ),
        ]
      ),
    ]
  );

  return savingsResultContainer;
};

export default ResultsSummary;
